import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  UpdateGST,
  createGST,
  getAllGST,
  getGstByID,
} from "./services/gst.services";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import { Button } from "@progress/kendo-react-buttons";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { clearGSTDetails } from "./gstSlice";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import { closed } from "../../components/muidrawer/muiDrawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";

interface GstChangeWatcherProps {
  formRenderProps: FormRenderProps;
}

const GstChangeWatcher: React.FC<GstChangeWatcherProps> = ({
  formRenderProps,
}) => {
  const gst = formRenderProps.valueGetter("gst");
  useEffect(() => {
    formRenderProps.onChange("sgst", { value: gst / 2 });
    formRenderProps.onChange("cgst", { value: gst / 2 });
    formRenderProps.onChange("igst", { value: gst });
  }, [gst]);
  return null;
};

const CreateGST: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const GstDetail = useAppSelector((state) => state.gst.GstDetail);
  const GstID = useAppSelector((state) => state.muidrawer.data);
  const formLoading = useAppSelector((state) => state.gst.formLoading);

  useEffect(() => {
    if (GstID) {
      dispatch(getGstByID(GstID));
    }
  }, [GstID]);

  useEffect(() => {
    return () => {
      dispatch(clearGSTDetails());
      dispatch(closed());
    };
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      if (!GstID) {
        const payload = {
          gst_title: values.gst_title,
          gst: values.gst,
          cgst: values.cgst,
          igst: values.igst,
          sgst: values.sgst,
          isactive: values.isactive === 1,
        };
        const response = await dispatch(createGST(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearGSTDetails());
          dispatch(getAllGST());
        }
      } else {
        const payload = {
          gst_guid: GstID,
          gst_title: values.gst_title,
          gst: values.gst,
          igst: values.igst,
          cgst: values.cgst,
          sgst: values.sgst,
          isactive: values.isactive === 1,
        };
        const response = await dispatch(UpdateGST(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(getAllGST());
          dispatch(closed());
          dispatch(clearGSTDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
    dispatch(clearGSTDetails());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={GstDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "17px 0 8px",
              }}
            >
              <Typography.h4 className="m-0">
                {GstID ? "Update GST" : "Add GST"}
              </Typography.h4>
              <Button
                type="button"
                fillMode={"flat"}
                themeColor={"dark"}
                style={{ marginRight: 4 }}
                onClick={() => dispatch(closed())}
              >
                <RxCross1 />
              </Button>
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="gst_title"
                label="GST Title"
                placeholder="GST Title"
                component={FormTextField}
                validator={requiredValidator} astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                wrapperClassName="right-alighned-field"
                name="gst"
                label="GST(%)"
                component={FormTextField}
                validator={requiredValidator} astrike={true}
                placeholder="0"
                type="number"
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                wrapperClassName="right-alighned-field"
                name="sgst"
                label="SGST(%)"
                placeholder="0"
                component={FormTextField}
                disabled
                type="number"
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                wrapperClassName="right-alighned-field"
                name="cgst"
                label="CGST(%)"
                placeholder="0"
                component={FormTextField}
                disabled
                type="number"
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                wrapperClassName="right-alighned-field"
                name="igst"
                label="IGST(%)"
                component={FormTextField}
                placeholder="0"
                disabled
                type="number"
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                id={"isactive"}
                name={"isactive"}
                label={"Status"}
                component={FormSelectionField}
                validator={requiredValidator} astrike={true}
                options={STATUSARRAY?.map((status: any) => {
                  return {
                    value: status.value,
                    label: status.label,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "start",
                marginTop: "20px",
                width: "100%",
              }}
            >
              <ButtonWithLoading
                label={GstID ? "Update" : "Save"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />
              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginLeft: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
            </GridLayoutItem>
            <GstChangeWatcher formRenderProps={formRenderProps} />
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateGST;
